// Developer → one project: its test cases (read-only) and every test run
// (batch) made from it. Opening a run shows the report table where the
// developer marks what they've fixed.
//
// Both lists are real tables now — search, filters, sortable columns and
// pagination. They were a raw <ul> of every case and a stack of buttons, which
// meant a 1,000-case project rendered 1,000 rows with no way to page through
// them and no way to open one and see what actually happened.
//
// The Test cases tab shows Priority, not "Last result". last_run_status is written
// ONLY by the cloud run-all path (ProjectRepository.update_case_review_status), and
// this page lists exe/extension batches — neither runner ever writes it, and the
// runner items carry no link back to the generated case. The column therefore read
// "not run yet" on every row forever, next to a Test runs tab showing 5 completed
// batches, which told developers their suite had never executed.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, FolderKanban, Globe, Loader2, FileText, Boxes, Bug,
  WifiOff, Puzzle, Bot, RotateCw,
} from "lucide-react";
import Aurora from "@/components/common/Aurora";
import DataTable from "@/components/dashboard/DataTable";
import CaseDetailModal from "@/components/shared/CaseDetailModal";
import DownloadReportButton from "@/components/developer/DownloadReportButton";
import { Badge, StatusBadge } from "@/components/ui/Badge";
import { cn } from "@/lib/cn";
import { getDevProject } from "@/api/developer";
import { formatDateTime } from "@/lib/dates";

const CATEGORY_STYLE = {
  functional: "bg-brand-500/10 text-brand-600 dark:text-brand-300",
  navigation: "bg-sky-500/10 text-sky-600 dark:text-sky-300",
  edge_case: "bg-amber-500/10 text-amber-600 dark:text-amber-300",
  ui: "bg-teal-500/10 text-teal-600 dark:text-teal-300",
};
// The generator emits far more categories than the four coloured above (validation,
// crud, auth, search, e2e, security…). An unknown one gets a neutral tone rather
// than borrowing "functional"'s colour, which made the badge colour carry no
// information at all.
const CATEGORY_FALLBACK = "bg-slate-500/10 text-slate-600 dark:text-slate-300";

const fmtWhen = (v) => {
  if (!v) return "—";
  return formatDateTime(v);
};

// How long the CURRENT run took: from started_at (reset on every re-run) to
// completed_at, or "so far" while it runs. Falls back to created_at for batches
// (and extension runs) that carry no started_at.
const batchSecs = (b) => {
  const start = b && (b.started_at || b.created_at);
  if (!start) return null;
  const end = b.completed_at ? new Date(b.completed_at).getTime() : Date.now();
  const s = (end - new Date(start).getTime()) / 1000;
  return s >= 0 ? s : null;
};
const fmtDur = (s) => {
  if (s == null) return "—";
  const m = Math.floor(s / 60), sec = Math.floor(s % 60);
  if (m >= 60) return `${Math.floor(m / 60)}h ${m % 60}m`;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

export default function DevProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("runs");   // runs | cases
  const [openCase, setOpenCase] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setData(await getDevProject(id));
    } catch (e) {
      setError((e && e.message) || "Couldn't load this project.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const batches = useMemo(() => data?.batches || [], [data]);
  const cases = useMemo(() => data?.test_cases || [], [data]);

  // THE "to fix" number, straight from the API — the same `_open_issues()` the
  // projects list uses for this project's card, so the two surfaces cannot report
  // different totals for one project.
  //
  // This used to be a JavaScript re-implementation of that rule over `batches`.
  // Keeping one business rule in two languages is what let the KPI and the rest of
  // the page disagree, and there was no way to tell which copy was authoritative.
  // The fallback keeps a client pointed at an older backend working rather than
  // rendering "undefined".
  const openIssues = useMemo(() => {
    if (typeof data?.open_issues === "number") return data.open_issues;
    const newest = {};
    for (const b of batches) {                        // API returns newest-first
      const current = newest[b.kind];
      if (!current || (!current.settled && b.settled)) newest[b.kind] = b;
    }
    return Object.values(newest).reduce((a, b) => a + (b.needs_fix || 0), 0);
  }, [data, batches]);

  // The per-run column and the KPI answer different questions, and the gap
  // between them is what made the two look broken. Stated once, under the table.
  const runsToFixTotal = useMemo(
    () => batches.reduce((a, b) => a + (b.needs_fix || 0), 0),
    [batches],
  );

  // Derived once so the table can sort and filter on them like any other column.
  const runRows = useMemo(() => batches.map((b) => {
    // `settled` counts every terminal state — blocked, cancelled, inconclusive and
    // orphaned included. Counting only passed/failed/errored left finished runs
    // stuck at "COMPLETED · 40%".
    const settled = b.settled ?? ((b.passed || 0) + (b.failed || 0) + (b.errored || 0));
    return {
      ...b,
      needs_fix: b.needs_fix ?? ((b.failed || 0) + (b.errored || 0)),
      progress: b.total ? Math.min(100, Math.max(0, Math.round((settled / b.total) * 100))) : 0,
    };
  }), [batches]);

  // Chips built from the data, not from a hardcoded four. The generator emits
  // ~15 categories (validation, crud, auth, e2e…), so a fixed list left most cases
  // unfilterable and offered "Ui", which the import path never produces.
  const categoryOptions = useMemo(
    () => [...new Set(cases.map((c) => c.category).filter(Boolean))].sort()
      .map((value) => ({ value, label: String(value).replace(/_/g, " ") })),
    [cases],
  );

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center text-slate-400">
        <Loader2 className="animate-spin" /> <span className="ml-2">Loading project…</span>
      </div>
    );
  }

  if (error) {
    return (
      <>
        <Link to="/developer" className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-brand-600 dark:text-slate-400">
          <ArrowLeft size={16} /> All projects
        </Link>
        <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <WifiOff size={15} /> {error}
        </div>
      </>
    );
  }

  return (
    <>
      <Link to="/developer" className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-brand-600 dark:text-slate-400">
        <ArrowLeft size={16} /> All projects
      </Link>

      {/* header */}
      <motion.div
        initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
        className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-soft dark:border-white/10 dark:bg-ink-900"
      >
        <Aurora className="opacity-50" />
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-3">
            <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-glow">
              <FolderKanban size={22} />
            </span>
            <div className="min-w-0">
              <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-white">{data?.name}</h1>
              {data?.base_url && (
                <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400">
                  <Globe size={14} className="shrink-0" /> <span className="truncate">{data.base_url}</span>
                </p>
              )}
            </div>
          </div>

          {/* The project-card "Download report" action was removed. Per-run
              downloads remain on each row of the Test runs table below, which is
              the unambiguous place to get a sheet: it names the run it belongs to
              instead of silently picking "the latest one". DownloadReportButton
              itself is untouched and still used there and on the batch report. */}
        </div>

        <div className="relative mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
          {[
            { icon: FileText, label: "Test cases", value: cases.length, tone: "text-sky-500 bg-sky-500/10" },
            { icon: Boxes, label: "Test runs", value: batches.length, tone: "text-brand-500 bg-brand-500/10" },
            { icon: Bug, label: "To fix", value: openIssues, tone: "text-rose-500 bg-rose-500/10" },
          ].map((s) => (
            <div key={s.label} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/70 px-4 py-3 dark:border-white/5 dark:bg-white/5">
              <span className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-lg", s.tone)}><s.icon size={16} /></span>
              <div className="min-w-0">
                <p className="truncate text-xs text-slate-400">{s.label}</p>
                <p className="text-lg font-bold text-slate-900 dark:text-white">{s.value}</p>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* tabs */}
      <div className="mb-4 mt-6 inline-flex rounded-xl border border-slate-200 bg-white p-1 dark:border-white/10 dark:bg-white/5">
        {[["runs", "Test runs", Boxes, batches.length],
          ["reruns", "Re-runs", RotateCw, batches.filter((b) => (b.rerun_count || 0) > 0).length],
          ["cases", "Test cases", FileText, cases.length]].map(([key, label, Icon, count]) => (
          <button key={key} onClick={() => setTab(key)}
            className={cn("inline-flex items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-sm font-medium transition-colors",
              tab === key ? "bg-brand-600 text-white shadow-soft" : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/5")}>
            <Icon size={15} /> {label} <span className="opacity-70">({count})</span>
          </button>
        ))}
      </div>

      {tab !== "cases" ? (
        <>
        <DataTable
          /* Distinct keys, or React reconciles the two branches as ONE DataTable
             instance and its internal search text follows you across the tabs —
             landing on "Test cases (412)" showing zero rows. */
          key={tab === "reruns" ? "reruns" : "runs"}
          columns={[
            {
              key: "name", header: "Test run",
              render: (b) => (
                <div className="flex min-w-0 items-center gap-3">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-300">
                    {b.kind === "extension" ? <Puzzle size={16} /> : <Bot size={16} />}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate font-medium text-slate-800 dark:text-slate-100">
                      {b.name || `Run ${String(b.id).slice(0, 8)}`}
                    </p>
                    <p className="text-xs text-slate-400">
                      {fmtWhen(b.created_at)}
                      {b.rerun_count > 0 && (
                        <span className="ml-1.5 inline-flex items-center gap-1 text-brand-600 dark:text-brand-300">
                          <RotateCw size={10} /> re-run ×{b.rerun_count}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              ),
            },
            {
              key: "kind", header: "Runner", sortable: true,
              render: (b) => (
                <Badge tone="slate">{b.kind === "extension" ? "Extension" : "Exe agent"}</Badge>
              ),
            },
            {
              key: "status", header: "Status", sortable: true,
              render: (b) => <StatusBadge status={b.status} />,
            },
            {
              key: "needs_fix", header: "To fix", sortable: true, align: "center",
              render: (b) => {
                if (b.needs_fix > 0) return <Badge tone="rose">{b.needs_fix}</Badge>;
                // A run that has produced nothing yet is not "all clear" — that
                // badge read identically for a green suite and one still queued.
                if (b.in_progress) return <Badge tone="slate">running…</Badge>;
                return <Badge tone="emerald">all clear</Badge>;
              },
            },
            {
              key: "progress", header: "Progress", align: "right",
              render: (b) => (
                <div className="flex items-center justify-end gap-2">
                  <div className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-200 dark:bg-white/10">
                    <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-500"
                         style={{ width: `${b.progress}%` }} />
                  </div>
                  <span className="text-xs tabular-nums text-slate-400">{b.progress}%</span>
                </div>
              ),
            },
            {
              // Time the current run took (or "so far" while live) — from started_at,
              // so a re-run shows its own minutes, not the wall-clock since creation.
              key: "duration", header: "Time", align: "right",
              render: (b) => {
                const s = batchSecs(b);
                return (
                  <span className="text-xs tabular-nums text-slate-400">
                    {fmtDur(s)}{s != null && !b.completed_at ? " so far" : ""}
                  </span>
                );
              },
            },
            {
              // Per-run sheet, without having to open the run first. stopPropagation
              // lives in the button — the row itself navigates on click.
              key: "report", header: "Report", align: "right",
              render: (b) => (
                <div className="flex justify-end">
                  <DownloadReportButton
                    compact
                    kind={b.kind}
                    batchId={b.id}
                    disabled={!b.total}
                    disabledHint="This run has no cases to report on"
                  />
                </div>
              ),
            },
          ]}
          rows={tab === "reruns" ? runRows.filter((b) => (b.rerun_count || 0) > 0) : runRows}
          searchKeys={["name", "id"]}
          filters={[
            { key: "status", label: "statuses",
              options: [...new Set(runRows.map((b) => b.status).filter(Boolean))] },
            { key: "kind", label: "runners", options: ["exe", "extension"] },
          ]}
          getRowId={(b) => `${b.kind}-${b.id}`}
          dateKey="created_at"
          dateLabel="Run date"
          pageSize={15}
          maxHeight="32rem"
          onRowClick={(b) => navigate(`/developer/projects/${id}/${b.kind}/${b.id}`)}
          emptyLabel={tab === "reruns"
            ? "No re-runs yet — when a batch is re-run to verify fixes, it shows up here with its re-run count and time."
            : "No test runs yet — when the tester runs this project, the report shows up here."}
        />
        {/* Reconciles the column with the KPI above. The column is per RUN, so a
            case that failed in three runs appears three times in it; "To fix" is
            per CASE, counted once on its latest result across every run and both
            runners. Without saying so, the two numbers read as a contradiction —
            which is exactly how this was reported. Only shown once there is a
            difference to explain. */}
        {/* Runs tab only — the Re-runs tab shows a filtered subset, and these
            totals are over every run, so they would not describe what is on screen. */}
        {tab === "runs" && batches.length > 0 && runsToFixTotal !== openIssues && (
          <p className="mt-2 text-xs text-slate-400">
            The column counts failures <span className="font-medium">per run</span> ({runsToFixTotal} in
            total across {batches.length} run{batches.length === 1 ? "" : "s"}).
            {" "}<span className="font-medium text-slate-500 dark:text-slate-300">To fix ({openIssues})</span>{" "}
            counts each test case once, on its latest result — so a case fixed since,
            or failing in several runs, is not counted twice.
          </p>
        )}
      </>
      ) : (
        <DataTable
          key="cases"
          columns={[
            {
              // The heading of a case is its test scenario; the instruction is
              // the composed, runnable version and only earns a line of its own
              // when it differs from that heading.
              key: "test_scenario", header: "Test case",
              render: (c) => (
                <div className="min-w-0 max-w-lg">
                  <p className="line-clamp-2 font-medium text-slate-800 dark:text-slate-100">
                    {c.test_scenario || c.instruction}
                  </p>
                  {c.test_scenario && c.instruction && c.instruction !== c.test_scenario && (
                    <p className="mt-0.5 line-clamp-1 text-xs text-slate-500 dark:text-slate-400">
                      {c.instruction}
                    </p>
                  )}
                  {c.expected_result && (
                    <p className="mt-0.5 line-clamp-1 text-xs text-slate-500 dark:text-slate-400">
                      <span className="font-medium">Expected:</span> {c.expected_result}
                    </p>
                  )}
                </div>
              ),
            },
            {
              // The report-sheet side of the case — latest agent result, the
              // dev's verdict and the one-line error, so a search hit on report
              // data also shows WHY it matched without opening the batch.
              key: "last_status", header: "Last result", sortable: true,
              render: (c) => {
                const status = c.last_status || c.last_run_status;
                return (
                  <div className="min-w-0 max-w-[16rem]">
                    {status ? <StatusBadge status={status} /> : <span className="text-xs text-slate-400">—</span>}
                    {c.last_dev_status && (
                      <p className="mt-1 truncate text-[0.6875rem] text-slate-500 dark:text-slate-400">
                        dev: <span className="capitalize">{c.last_dev_status}</span>
                        {c.last_batch_name && <span className="ml-1">· {c.last_batch_name}</span>}
                      </p>
                    )}
                    {c.last_last_error && (
                      <p className="mt-0.5 line-clamp-1 text-[0.6875rem] text-rose-600 dark:text-rose-400"
                         title={c.last_last_error}>
                        {c.last_last_error}
                      </p>
                    )}
                    {c.last_dev_note && (
                      <p className="mt-0.5 line-clamp-1 text-[0.6875rem] text-slate-400" title={c.last_dev_note}>
                        {c.last_dev_note}
                      </p>
                    )}
                  </div>
                );
              },
            },
            {
              key: "category", header: "Category", sortable: true,
              render: (c) => (
                <span className={cn("rounded-md px-2 py-0.5 text-[0.625rem] font-semibold uppercase",
                                    CATEGORY_STYLE[c.category] || CATEGORY_FALLBACK)}>
                  {String(c.category || "functional").replace(/_/g, " ")}
                </span>
              ),
            },
            {
              key: "page_url", header: "Page",
              render: (c) => (
                <span className="line-clamp-1 max-w-xs font-mono text-xs text-slate-400" title={c.page_url}>
                  {c.page_url}
                </span>
              ),
            },
            {
              key: "priority", header: "Priority", sortable: true,
              render: (c) => (
                c.priority
                  ? <Badge tone="slate" className="capitalize">{c.priority}</Badge>
                  : <span className="text-xs text-slate-400">—</span>
              ),
            },
          ]}
          rows={cases}
          searchKeys={[
            // the case's own sheet fields
            "test_scenario", "instruction", "test_description", "preconditions",
            "test_data", "page_url", "expected_result", "category", "priority",
            "module", "test_type", "verify_text", "account", "id",
            // report-sheet data — what the agent found and the dev claimed
            "last_status", "last_run_status", "last_dev_status", "last_dev_note",
            "last_last_error", "last_batch_name", "last_batch_kind",
            // The sheet's own TC id ("TC-014") — the reference a developer is
            // most likely to be handed, and the one thing search couldn't find.
            "ext_test_id",
          ]}
          filters={[
            { key: "category", label: "categories", options: categoryOptions },
          ]}
          pageSize={25}
          maxHeight="32rem"
          onRowClick={setOpenCase}
          emptyLabel="No test cases have been generated for this project yet."
        />
      )}

      {/* Any case can now be opened in full — instruction, expected result,
          steps and screenshots. Before this the list was a dead end.
          `run_id` and `kind` come from the case's latest report row: without
          them the modal has no run to read, so a case that failed opened onto
          "not run yet", and a hardcoded "cloud" sent every screenshot request
          to the wrong runner's endpoint. */}
      <CaseDetailModal
        testCase={openCase && { ...openCase, run_id: openCase.last_run_id }}
        kind={openCase?.last_batch_kind || "cloud"}
        projectId={id}
        onClose={() => setOpenCase(null)}
      />

    </>
  );
}
